/**
 * Stop identity for grouping manual stops.
 *
 * Every customer stop is stored with dealer_name = 'Customer stop' (and dealer_id ''),
 * so grouping by name would merge different households into one stop. Customer stops
 * are identified by their contact (one household), falling back to the full address
 * including unit. Business stops keep grouping by location name, as before.
 */

export const CUSTOMER_STOP_LABEL = 'Customer stop';

export interface StopLocationFields {
  stop_kind?: string | null | undefined;
  contact_id?: string | null | undefined;
  dealer_name?: string | null | undefined;
  dealer_address?: string | null | undefined;
  address_line2?: string | null | undefined;
  dealer_postal_code?: string | null | undefined;
}

export function isCustomerStop(stop: Pick<StopLocationFields, 'stop_kind'>): boolean {
  return stop.stop_kind === 'customer';
}

const norm = (v: string | null | undefined) => (v ?? '').trim().toUpperCase().replace(/\s+/g, ' ');

/**
 * Stable grouping key: `customer:<contact_id>`, `customer-address:<addr|unit|zip>`,
 * or `business:<name>`.
 */
export function getStopLocationKey(stop: StopLocationFields): string {
  if (isCustomerStop(stop)) {
    if (stop.contact_id) return `customer:${stop.contact_id}`;
    return `customer-address:${norm(stop.dealer_address)}|${norm(stop.address_line2)}|${norm(
      stop.dealer_postal_code
    )}`;
  }
  return `business:${stop.dealer_name || 'Unknown Location'}`;
}

/** What to call the location in lists and tooltips — never a customer's name. */
export function getStopDisplayName(stop: StopLocationFields): string {
  if (isCustomerStop(stop)) return CUSTOMER_STOP_LABEL;
  return stop.dealer_name || 'Unknown Location';
}
